import { Link, createFileRoute } from "@tanstack/react-router";
import { ExpandableIntro } from "@/components/expandable-intro";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { CasinoList } from "@/components/casino-list";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks, HowWeTested } from "@/components/eeat-blocks";
import { PayoutSpeedInfographic } from "@/components/infographics";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/best-uitbetalende";
import { TEST_IMAGE } from "@/content/images";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { getAuthor } from "@/lib/authors";
import { ogImageMeta } from "@/lib/og";
import { getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";

const PATH = "/best-uitbetalende-online-casinos";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/best-uitbetalende-online-casinos")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "Best Uitbetalende Casino 2026: Hoogste RTP-Slots en Kassa's" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "Best Uitbetalende Casino 2026: Hoogste RTP-Slots en Kassa's" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("best-uitbetalende"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Best uitbetalende casino's", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: BestPayoutPage,
});

function BestPayoutPage() {
  const casinos = getPublicCasinos();
  const budget = new LinkBudget(PATH);

  const takeaways = [
    "We inventariseerden bij 10 vergunde aanbieders de RTP van de 100 meest gespeelde titels.",
    "De gewogen uitbetalingspercentages liepen uiteen van 94,8% tot 96,8%.",
    "Het verschil tussen spelcategorieën is groter dan het verschil tussen aanbieders.",
    "Blackjack en video poker betalen rekenkundig het beste uit; progressieve jackpots het slechtst.",
    "Dezelfde slottitel draait per aanbieder soms op een lagere variant: controleer de spelinformatie.",
    "Een bonus met hoge inzetvereiste verlaagt in de praktijk je effectieve rendement.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Best uitbetalende casino&apos;s</span>
      </nav>

      <h1 className="max-w-4xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        Best uitbetalende online casino&apos;s: het uitbetalingspercentage per aanbieder
      </h1>

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>{guide.description}</ExpandableIntro>

      <div className="mt-8 hidden max-w-3xl sm:block">
        <KeyTakeaways items={takeaways} />
      </div>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">
              Top 10 best uitbetalende online casino&apos;s
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Dezelfde vergelijkingslijst als elders op de site. Het gewogen uitbetalingspercentage
              per aanbieder vind je in de RTP-tabel verderop in dit artikel.
            </p>
            <div className="mt-4">
              <CasinoList casinos={casinos} />
            </div>
            <figure className="mt-6 overflow-hidden rounded-xl border border-border">
              <img
                src={TEST_IMAGE.src}
                alt={TEST_IMAGE.alt}
                width={1280}
                height={720}
                loading="lazy"
                decoding="async"
                className="aspect-[21/9] w-full object-cover"
              />
              <figcaption className="border-t border-border bg-secondary/50 px-4 py-2 text-xs text-muted-foreground">
                Beeld: redactie {SITE.name}
              </figcaption>
            </figure>
          </section>

          <div className="mt-10">
            <HowWeTested
              subject="de uitbetalingspercentages en de kassa van elke aanbieder"
              providers={10}
              period="november 2026"
            />
          </div>

          <section className="mt-12" aria-label="Alles over uitbetalingspercentages">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14 grid gap-6">
            <PayoutSpeedInfographic />
          </div>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="uitbetalingspercentages"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over uitbetalingspercentages
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks authorSlug={guide.authorSlug} reviewerSlug={guide.reviewerSlug} updatedAt={guide.updatedAt} />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
